import type { JSX } from 'react';
import isEmpty from 'lodash/isEmpty';
import React, { useState } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';

import { FormBlock, StepProps } from '@/widgets/ServiceFormTemplate';

import { buildDocumentPreviewRows } from '@/features/ui/DocumentsData/model/preview';

import { ConditionView, Grid, StepControlPanel, Loader } from '@/shared/ui';
import { AddressBlock } from '@/shared/ui/AddressBlock';

import { DocumentTypeCode } from '@/api/types';

import { DocumentsData } from '../../DocumentsData';

import { usePlaceOfResidenceDefaults } from './hooks/usePlaceOfResidenceDefaults';
import { WeaponPlaceOfResidenceSchema } from './model/types';

import { AddressWarningModal } from './AddressWarningModal';

export const WeaponPlaceOfResidence: React.FC<StepProps> = ({ next, prev, currentRequestId, onStepDraftSave }) => {
  const { control } = useFormContext<WeaponPlaceOfResidenceSchema>();
  const { getValues } = useFormContext();
  const requestId = currentRequestId ?? '';

  const { documentTypeEntities, isDocumentTypesLoading, isUkraine } = usePlaceOfResidenceDefaults();

  const [isWarningModalOpen, setIsWarningModalOpen] = useState(false);

  const registeredAddress = getValues('registeredAddress');
  const actualAddress = getValues('actualAddress') ?? registeredAddress;

  const registeredNewAddress = useWatch({ control, name: 'registeredNewAddress' as const });

  const normalize = (value?: string | null): string => {
    return (value ?? '').trim().toUpperCase();
  };

  type Address = typeof registeredAddress;

  const areAddressesEqual = (a?: Address, b?: Address): boolean => {
    if (!a || !b) return false;

    return (
      a.country?.id === b.country?.id &&
      a.region?.id === b.region?.id &&
      a.district?.id === b.district?.id &&
      a.settlement?.id === b.settlement?.id &&
      a.street?.id === b.street?.id &&
      normalize(a.house) === normalize(b.house) &&
      normalize(a.building) === normalize(b.building) &&
      normalize(a.apartment) === normalize(b.apartment)
    );
  };

  const isSameAsRegistered = areAddressesEqual(registeredAddress, registeredNewAddress);
  const isSameAsActual = areAddressesEqual(actualAddress, registeredNewAddress);

  const handleNext = () => {
    if (isSameAsRegistered || isSameAsActual) {
      setIsWarningModalOpen(true);
      return;
    }
    next?.();
  };

  const closeModal = () => setIsWarningModalOpen(false);

  return (
    <ConditionView if={!isDocumentTypesLoading} else={<Loader />}>
      <FormBlock title="Нова адреса: Місце проживання (зберігання зброї)">
        <Controller
          name="registeredNewAddress"
          control={control}
          render={({ field, formState: { errors } }): JSX.Element => (
            <AddressBlock
              address={field.value}
              onlyUkraine={isUkraine}
              revalidate={!isEmpty(errors)}
              handleAddressChange={(newAddress): void => {
                field.onChange(newAddress);
              }}
            />
          )}
        />
      </FormBlock>
      <FormBlock title="Дані документу, який підтверджує зареєстроване місце проживання (зберігання зброї)">
        <DocumentsData
          maxDocumentsCount={1}
          withUploader
          request_id={requestId}
          onSaveDraft={onStepDraftSave}
          documentsRequired
          autofillType={false}
          autofillInfo={false}
          documentTypeEntities={
            documentTypeEntities?.filter(
              (documentEntity) => documentEntity.code !== DocumentTypeCode.VITYAG_TERITORIALNOYI_GROMADI,
            ) ?? []
          }
          name="declaredNewDocRent"
        />
      </FormBlock>
      <StepControlPanel next={handleNext} prev={prev} />
      <AddressWarningModal isModalOpen={isWarningModalOpen} closeModal={closeModal} />
    </ConditionView>
  );
};

export const WeaponPlaceOfResidencePreview: React.FC = () => {
  const { getValues } = useFormContext<WeaponPlaceOfResidenceSchema>();
  const { registeredNewAddress, declaredNewDocRent } = getValues();

  type Address = WeaponPlaceOfResidenceSchema['registeredNewAddress'];

  const formatAddress = (address?: Address): Array<[string, Nullable<string>]> => [
    ['Країна', address?.country?.label ?? null],
    ['Область', address?.region?.label ?? null],
    ['Район', address?.district?.label ?? null],
    ['Населений пункт', address?.settlement?.label ?? null],
    ['Вулиця', address?.street?.label ?? null],
    ['Номер будинку', address?.house ?? null],
    ['Номер корпусу', address?.building ?? null],
    ['Номер квартири', address?.apartment ?? null],
  ];

  const formatDocument = (docs?: WeaponPlaceOfResidenceSchema['declaredNewDocRent'], attachmentsLabel?: string) => {
    const doc = Array.isArray(docs) ? docs[0] : undefined;
    return buildDocumentPreviewRows(doc, attachmentsLabel);
  };

  const registeredNewAddressRows = formatAddress(registeredNewAddress);

  const declaredDocRows = formatDocument(
    declaredNewDocRent,
    'Документ, який підтверджує нове місце проживання (зберігання зброї)',
  );

  return (
    <>
      <Grid title="Нова адреса: Місце проживання (зберігання зброї)" data={registeredNewAddressRows} />
      <Grid
        title="Дані документу, який підтверджує зареєстроване місце проживання (зберігання зброї)"
        data={declaredDocRows}
      />
    </>
  );
};
